import "./styles.scss";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import classnames from "classnames";

import { useApp } from "context/app";
import { useTasks } from "context/client/tasks";

import Table from "components/Table";
import TaskOnboarding from "components/TaskOnboarding";
import PlaceholderPage from "components/PlaceholderPage";
import PillGridNew from "components/PillGridNew";
import TaskInfo from "components/TaskInfo";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";

const mainClass = "tasks-client-table";

const TasksTable = ({
  task_onboarding,
  setExplanation,
  onOnboardingClose,
  isSingleWebsite,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { websites } = useApp();
  const { data, loading, filters, filterHandler, updateTask } = useTasks();

  const table_data = {
    keys: [
      {
        key: "created_at",
        value: t("Creation date"),
      },
      {
        key: "website",
        sort_key: isSingleWebsite ? false : "website",
        value: t("Website [SHORT]"),
      },
      {
        sort_key: "title",
        key: "title-full",
        value: t("Title"),
      },
      {
        key: "status",
        value: t("Status"),
      },
      {
        key: "priority",
        value: t("Priority"),
      },
      {
        key: "time_tracked",
        value: t("Work time"),
      },
      {
        key: "details",
        sort_key: false,
        value: t("Details"),
      },
    ],
    items: data?.map(
      ({ _id, created_at, title, website, status, priority, total_time }) => {
        const status_data = task_status_data?.find(
          (item) => item?.value === status,
        );
        const priority_data = task_priority_data?.find(
          (item) => item?.value === priority,
        );

        return [
          {
            key: "id",
            type: "hidden_id",
            value: _id,
          },
          {
            key: "created_at",
            type: "text",
            value: moment(created_at).format("LL"),
          },
          {
            key: "website",
            type: "link",
            value: {
              label: website?.name,
              href: `/websites/${website?._id}`,
            },
          },
          {
            key: "title-full",
            type: "link",
            value: {
              label: title,
              href: `/tasks/${_id}`,
            },
          },
          {
            key: "status",
            type: "pill",
            value: {
              data: {
                value: status_data?.value,
                label: t(status_data?.label),
                color: status_data?.color,
              },
              options: task_status_data
                .filter((item) => item?.key !== "canceled")
                .map((item) => ({
                  ...item,
                  label: t(item.label),
                })),
              optionsFooter: (
                <TaskInfo onClick={() => setExplanation("status")} />
              ),
              isCompact: true,
              onChange: (option) => updateTask(_id, { status: option.value }),
            },
          },
          {
            key: "priority",
            type: "pill",
            value: {
              data: {
                value: priority_data?.value,
                label: t(priority_data?.label),
                color: priority_data?.color,
              },
              icon: "flag",
              options: task_priority_data.map((item) => ({
                ...item,
                label: t(item.label),
              })),
              optionsFooter: (
                <TaskInfo
                  type="priority"
                  onClick={() => setExplanation("priority")}
                />
              ),
              isCompact: true,
              onChange: (option) => updateTask(_id, { priority: option.value }),
            },
          },
          {
            key: "time_tracked",
            value: !!total_time?.$numberDecimal
              ? toHoursAndMinutes(total_time?.$numberDecimal)?.formatted
              : "-",
          },
          {
            key: "details",
            type: "clickable_text",
            value: {
              label: t("View"),
              onclick: (_, onboarding) =>
                navigate(
                  `/tasks/${_id}${onboarding ? "?onboarding=task" : ""}`,
                ),
            },
          },
        ];
      },
    ),
  };

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--empty`]: !(websites?.length > 0),
        })}
      >
        {websites?.length > 0 ? (
          <Table
            data={table_data}
            loading={!!loading}
            task_onboarding={task_onboarding}
            sort={filters?.sort}
            setSort={(value) => filterHandler("sort", value)}
            filters_new={
              <div className={`${mainClass}__filters`}>
                <div>
                  <PillGridNew
                    header="Status"
                    label={t("Select status")}
                    selected={filters?.status}
                    options={task_status_data
                      .filter((item) => item?.key !== "canceled")
                      .map((item) => ({
                        ...item,
                        label: t(item.label),
                      }))}
                    optionsFooter={
                      <TaskInfo onClick={() => setExplanation("status")} />
                    }
                    onChange={(option) =>
                      filterHandler("status", option.key, true)
                    }
                  />
                </div>
                <div>
                  <PillGridNew
                    header={t("Priority")}
                    label={t("Select priority")}
                    selected={filters?.priority}
                    icon="flag"
                    options={task_priority_data.map((item) => ({
                      ...item,
                      label: t(item.label),
                    }))}
                    optionsFooter={
                      <TaskInfo
                        type="priority"
                        onClick={() => setExplanation("priority")}
                      />
                    }
                    onChange={(option) =>
                      filterHandler("priority", option.key, true)
                    }
                  />
                </div>
                <div>
                  <PillGridNew
                    header={t("Tasks with time exceeding monthly plan")}
                    label={t("Show tasks")}
                    selected={filters?.time_over}
                    onChange={() =>
                      filterHandler(
                        "time_over",
                        filters?.time_over === "true" ? "" : "true",
                      )
                    }
                  />
                </div>
              </div>
            }
          />
        ) : (
          <PlaceholderPage
            title={t("Add your website to Webinity")}
            text={t(
              "Just a few simple steps away from peace of mind with managing your website or store. Let us take care of your website, and you enjoy the peace and time to grow your business.",
            )}
            button={{
              label: t("Add website"),
              href: "/websites/new",
            }}
          />
        )}
      </div>

      {!!task_onboarding && !!!loading && (
        <TaskOnboarding
          init_step={8}
          task_id={data?.[0]?._id}
          onClose={onOnboardingClose}
        />
      )}
    </>
  );
};

export default TasksTable;
